import { skipToken } from '@reduxjs/toolkit/query';
import { baseRestApi, getRestFailureUserMessage } from '@src/utils/rtkQuery';
import { CareOrder, NotificationVariants, type ToastProps, type useToasts } from '@talos/kyoko';
import { useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

interface WorkingOrderPreviewRequestBody {
  ClOrdId: string;
  MemberCareOrders: string[];
}

interface WorkingOrderModificationPreviewRequestBody extends WorkingOrderPreviewRequestBody {
  OrderID: string;
}

export const CareOrderNettingPreviewMode = {
  Create: 'create',
  Modify: 'modify',
} as const;
export type CareOrderNettingPreviewMode =
  (typeof CareOrderNettingPreviewMode)[keyof typeof CareOrderNettingPreviewMode];

const workingOrderPreviewEndpoints = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    previewWorkingOrder: builder.query<CareOrder[], WorkingOrderPreviewRequestBody>({
      query: body => ({
        method: 'POST',
        url: '/care/working/orders/preview',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
      transformResponse: (response: CareOrder[]) => response.map(entry => new CareOrder(entry)),
      keepUnusedDataFor: 0,
    }),
    previewWorkingOrderModification: builder.query<CareOrder[], WorkingOrderModificationPreviewRequestBody>({
      query: body => ({
        method: 'PATCH',
        url: '/care/working/orders/preview',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
      transformResponse: (response: CareOrder[]) => response.map(entry => new CareOrder(entry)),
      keepUnusedDataFor: 0,
    }),
  }),
});

const { usePreviewWorkingOrderQuery, usePreviewWorkingOrderModificationQuery } = workingOrderPreviewEndpoints;

type AddToast = ReturnType<typeof useToasts<ToastProps>>['add'];

/**
 * Fetches a netting preview and exposes the resulting working order state.
 *
 * The API returns preview care orders; this hook surfaces the first valid order as
 * the netted working order, plus loading/error state for the dialog.
 *
 * @param mode Netting preview mode (`create` or `modify`).
 * @param careOrderIDs Selected care order IDs used as netting inputs.
 * @param orderID Existing netted order ID to modify when in modify mode.
 * @param addToast Optional toast dispatcher used to surface preview errors.
 */
export function useWorkingOrderPreview({
  mode = CareOrderNettingPreviewMode.Create,
  careOrderIDs,
  orderID,
  addToast,
}: {
  mode?: CareOrderNettingPreviewMode;
  careOrderIDs: string[];
  orderID?: string;
  addToast?: AddToast;
}) {
  const hasEnoughCareOrdersToPreview = careOrderIDs.length >= 1;
  const canPreviewCreation = mode === CareOrderNettingPreviewMode.Create && hasEnoughCareOrdersToPreview;
  const canPreviewModification =
    mode === CareOrderNettingPreviewMode.Modify && hasEnoughCareOrdersToPreview && orderID != null;

  // Generate a fresh ClOrdId whenever the selected care orders change.
  const createRequest = useMemo<WorkingOrderPreviewRequestBody | typeof skipToken>(() => {
    if (!canPreviewCreation) {
      return skipToken;
    }

    return {
      ClOrdId: uuid(),
      MemberCareOrders: careOrderIDs,
    };
  }, [careOrderIDs, canPreviewCreation]);

  const modifyRequest = useMemo<WorkingOrderModificationPreviewRequestBody | typeof skipToken>(() => {
    if (!canPreviewModification) {
      return skipToken;
    }

    return {
      ClOrdId: uuid(),
      OrderID: orderID,
      MemberCareOrders: careOrderIDs,
    };
  }, [canPreviewModification, careOrderIDs, orderID]);

  // Normalize backend errors into a user-friendly message for UI and toast usage.
  const createQuery = usePreviewWorkingOrderQuery(createRequest);
  const modifyQuery = usePreviewWorkingOrderModificationQuery(modifyRequest);
  const { data, isLoading, isFetching, isError, error } =
    mode === CareOrderNettingPreviewMode.Modify ? modifyQuery : createQuery;
  const previewError = isError ? getRestFailureUserMessage(error) : undefined;

  // Show toast once a preview request fails and we have a meaningful error message.
  useEffect(() => {
    if (!isError || addToast == null) {
      return;
    }

    if (previewError == null) {
      return;
    }

    const previewOperationLabel = mode === CareOrderNettingPreviewMode.Modify ? 'netting modification' : 'netted order';
    addToast({
      text: `Could not preview ${previewOperationLabel}: ${previewError}`,
      variant: NotificationVariants.Negative,
    });
  }, [isError, previewError, addToast, mode]);

  if (!canPreviewCreation && !canPreviewModification) {
    return {
      workingOrder: undefined,
      loading: false,
      previewError: undefined,
    };
  }

  const loading = isLoading || isFetching;
  // Preserve error details for callers when preview fetch fails.
  if (isError) {
    return {
      workingOrder: undefined,
      loading,
      previewError,
    };
  }

  // Only expose preview order when backend returned a persisted working order ID.
  const workingOrder = data?.at(0);
  if (workingOrder && workingOrder.OrderID != null) {
    return { workingOrder, loading, previewError: undefined };
  }

  return {
    workingOrder: undefined,
    loading,
    previewError: undefined,
  };
}
